import { useState } from "react";

import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AmsBookingStatus, ClientPersonalDataResponse, clientsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface UseAmsAssignmentParams {
  clientId: string;
  /** The persisted client data, i.e. exactly what the booking endpoint will read. Undefined while loading. */
  personalData: ClientPersonalDataResponse | undefined;
  isFormDirty: boolean;
}

/**
 * Drives the "AMS Zuweisung durchführen" action. The endpoint takes only the client id and books from
 * the persisted client row, so the preconditions are checked against those same persisted values —
 * never against the form, which may hold edits the booking cannot see. Unsaved edits therefore block
 * the action instead of being validated: validating them would let a changed PLZ pass the check while
 * the VID is still resolved from the stored one, landing the client in the wrong Veranstaltung.
 */
export function useAmsAssignment({ clientId, personalData, isFormDirty }: UseAmsAssignmentParams) {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useAlertContext();
  const queryClient = useQueryClient();

  // Blocks the button right after a booking without refetching the personal data: reloading it would
  // reinitialize the form and discard any edits the user has not saved yet.
  const [bookedInSession, setBookedInSession] = useState(false);
  const [outcomePendingInSession, setOutcomePendingInSession] = useState(false);

  const isBooked = personalData?.blockingAmsBookingStatus === AmsBookingStatus.Succeeded || bookedInSession;

  // An attempt whose outcome the AMS never confirmed may exist in the portal, so the backend refuses
  // the next one until it is resolved — the button has to say so rather than run into that refusal.
  const isOutcomePending =
    personalData?.blockingAmsBookingStatus === AmsBookingStatus.Pending || outcomePendingInSession;

  const isDisabled = isBooked || isOutcomePending || isFormDirty || personalData === undefined;

  const missingDataMessage = (data: ClientPersonalDataResponse): string | null => {
    if (!data.firstName.trim()) {
      return t("clients.client.masterData.personalData.amsAssignment.firstNameMissing");
    }
    if (!data.lastName.trim()) {
      return t("clients.client.masterData.personalData.amsAssignment.lastNameMissing");
    }
    if (!data.insuranceNumber?.trim()) {
      return t("clients.client.masterData.personalData.amsAssignment.insuranceNumberMissing");
    }
    if (!data.zipCode.trim()) {
      return t("clients.client.masterData.personalData.amsAssignment.zipCodeMissing");
    }
    return null;
  };

  // Every rejection the endpoint can return carries a code; without a case here it would reach the
  // counselor as the generic failure, which names nothing they could act on. The two configuration
  // errors in particular are fixed in the AMS-Parameter settings, not on this page.
  const errorMessage = (code: string | undefined): string => {
    switch (code) {
      case ApiErrorCode.AmsBookingAlreadyBooked:
        return t("clients.client.masterData.personalData.amsAssignment.alreadyBooked");
      case ApiErrorCode.AmsBookingPending:
        return t("clients.client.masterData.personalData.amsAssignment.outcomePending");
      case ApiErrorCode.AmsBookingFirstNameMissing:
        return t("clients.client.masterData.personalData.amsAssignment.firstNameMissing");
      case ApiErrorCode.AmsBookingLastNameMissing:
        return t("clients.client.masterData.personalData.amsAssignment.lastNameMissing");
      case ApiErrorCode.AmsBookingInsuranceNumberMissing:
        return t("clients.client.masterData.personalData.amsAssignment.insuranceNumberMissing");
      case ApiErrorCode.AmsBookingInsuranceNumberNotUsable:
        return t("clients.client.masterData.personalData.amsAssignment.insuranceNumberNotUsable");
      case ApiErrorCode.AmsBookingZipCodeMissing:
        return t("clients.client.masterData.personalData.amsAssignment.zipCodeMissing");
      case ApiErrorCode.AmsBookingSidNotConfigured:
        return t("clients.client.masterData.personalData.amsAssignment.sidNotConfigured");
      case ApiErrorCode.AmsBookingNoVidForZipCode:
        return t("clients.client.masterData.personalData.amsAssignment.noVidForZipCode");
      case ApiErrorCode.AmsBookingRequestFailed:
        return t("clients.client.masterData.personalData.amsAssignment.requestFailed");
      case ApiErrorCode.AmsBookingWritesDisabled:
        return t("clients.client.masterData.personalData.amsAssignment.writesDisabled");
      default:
        return t("clients.client.masterData.personalData.amsAssignment.error");
    }
  };

  const booking = useMutation({
    mutationFn: () => clientsApi.createAmsBooking({ clientId }, withLocalErrorHandling),
    onSuccess: (response) => {
      if (response.status === AmsBookingStatus.Failed) {
        showError(response.message ?? t("clients.client.masterData.personalData.amsAssignment.error"));
        return;
      }

      // The booking was sent but the AMS never confirmed it. It stays on record as unresolved, and no
      // Aktenvermerk is written for it — only a confirmed assignment is documented.
      if (response.status === AmsBookingStatus.Pending) {
        setOutcomePendingInSession(true);
        showWarning(response.message ?? t("clients.client.masterData.personalData.amsAssignment.unconfirmed"));
        return;
      }

      setBookedInSession(true);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
      showSuccess(t("clients.client.masterData.personalData.amsAssignment.success"));
    },
    // The server refuses a second booking for a client that already has one or still has an unresolved
    // one, which this page only learns about on load: another tab or another counselor may have booked
    // meanwhile. Reflect the refusal in the button instead of only reporting it, so the page stops
    // offering the action.
    onError: (error) => {
      void getApiErrorCode(error).then((code) => {
        if (code === ApiErrorCode.AmsBookingAlreadyBooked) {
          setBookedInSession(true);
        } else if (code === ApiErrorCode.AmsBookingPending) {
          setOutcomePendingInSession(true);
        }

        showError(errorMessage(code));
      });
    },
  });

  const performAssignment = () => {
    if (personalData === undefined || isBooked || isOutcomePending || isFormDirty) {
      return;
    }

    const missing = missingDataMessage(personalData);
    if (missing) {
      showError(missing);
      return;
    }

    booking.mutate();
  };

  return {
    performAssignment,
    isAssigning: booking.isPending,
    isBooked,
    isOutcomePending,
    isDisabled,
  };
}
